import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PaymentFormInputs } from '../types'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { Banner, Button, Form, InlineMessage, TextField, Toggle, popSuccessToast } from '@boltenergy-be/design-system'
import { ContractResponseCodes } from 'types/errorCodes.ts'
import { updateAccount } from 'store/contact/thunks'
import { ThunkDefaultErrorMsg } from 'store/contact/thunks/types.ts'
import styles from './PaymentForm.module.scss'
import { useNavigate } from 'react-router'
import { routes } from 'types/routes.ts'
import { selectCurrentAccount } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { Iban } from 'components/form-fields'
import { IS_PROD_TESTING } from 'constants/envs.ts'

const PaymentForm = () => {
  // Redux
  const dispatch = useStoreDispatch()
  const { paymentDetails, contactSfId, sfId } = useStoreSelector(selectCurrentAccount)
  const { error, loading } = useStoreSelector((store) => store.contact)

  // i18n
  const { t } = useTranslation(['user', 'common', 'errors'])

  // Router
  const navigate = useNavigate()

  // Constants
  const mandateNumber = paymentDetails?.mandateNumber

  // React hook form
  const hookForm = useForm<PaymentFormInputs>({
    mode: 'onBlur',
    defaultValues: {
      directDebitPaymentMode: paymentDetails?.directDebit,
      iban: paymentDetails?.iban
    }
  })
  const {
    handleSubmit,
    watch,
    control,
    formState: { errors }
  } = hookForm
  const watchDirectDebitPaymentMode = watch('directDebitPaymentMode')

  /**
   * Handles the form submission after validation by React Hook Form
   *
   * @param {PaymentFormInputs} data
   */
  const onSubmit = async (data: PaymentFormInputs) => {
    // Update the account (only map the fields that have changed since it's a PATCH request)
    const res = await dispatch(
      updateAccount({
        accountId: sfId,
        contactId: contactSfId,
        account: {
          paymentDetails: {
            iban: data.iban,
            directDebit: data.directDebitPaymentMode
          }
        }
      })
    )

    if (res.meta.requestStatus === 'fulfilled') {
      trackEvent({ category: 'user', object: 'edit_payment_info', action: 'submit' })
      void navigate(routes.USER_PAYMENT)
      popSuccessToast(
        t(($) => $.formFeedback.saved, {
          ns: 'common'
        })
      )
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className={styles['payment-form']}>
      <Banner type="informative">{t(($) => $.payment.banner)}</Banner>

      <div className={styles['toggle-wrapper']}>
        <label>{t(($) => $.payment.fields.paymentMethod.title)}</label>
        <Controller
          name="directDebitPaymentMode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Toggle
              isFullwidth
              active={value}
              onClick={onChange}
              options={[
                { value: false, label: t(($) => $.payment.fields.paymentMethod.bankTransfer) },
                { value: true, label: t(($) => $.payment.fields.paymentMethod.directDebit) }
              ]}
            />
          )}
        />
      </div>

      <FormProvider {...hookForm}>
        <Iban required description={t(($) => $.payment.fields.financial.description)} />
      </FormProvider>

      {watchDirectDebitPaymentMode && (
        <TextField label={t(($) => $.payment.fields.financial.mandate)} disabled name="mandate" value={mandateNumber ?? '/'} />
      )}

      <Button isFullwidth type="submit" disabled={IS_PROD_TESTING || Object.keys(errors).length > 0} loading={loading}>
        {t(($) => $.save, {
          ns: 'common'
        })}
      </Button>

      {!loading &&
        error &&
        [ThunkDefaultErrorMsg.UPDATE_ACCOUNT as string, ContractResponseCodes.MERCATOR_CUSTOMER_NOT_FOUND].includes(error) && (
          <InlineMessage type="negative">
            {error === ContractResponseCodes.MERCATOR_CUSTOMER_NOT_FOUND
              ? t(($) => $.mercator.notFound, {
                  ns: 'errors'
                })
              : t(($) => $.default, {
                  ns: 'errors'
                })}
          </InlineMessage>
        )}
    </Form>
  )
}

export default PaymentForm
